import { useEffect, useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/Reveal";
import { takeInquiryPrefill, PREFILL_EVENT } from "@/lib/inquiryPrefill";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = ["Brand Development", "Creative Direction", "Design", "Full Brand World", "Something Else"];
const timelines = ["Immediately", "1–3 months", "3–6 months", "Flexible"];
const budgets = ["Under $10k", "$10k–$25k", "$25k–$50k", "$50k+", "Prefer to discuss"];

const Label = ({ children, optional }) => (
  <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal-mute mb-1">
    {children}
    {optional && <span className="ml-2 normal-case tracking-normal italic">(optional)</span>}
  </label>
);

export const Inquiry = () => {
  const [form, setForm] = useState({
    name: "", email: "", brand: "", project_type: projectTypes[0],
    scope: "", timeline: timelines[1], budget: "", agreed: false,
  });
  const [status, setStatus] = useState("idle");
  const [extraType, setExtraType] = useState(null);

  // Carried-in service or project from the Edit Menu / project pages — lands as an editable selection
  useEffect(() => {
    const apply = (label) => {
      if (!label) return;
      if (!projectTypes.includes(label)) setExtraType(label);
      setForm((f) => ({ ...f, project_type: label }));
    };
    apply(takeInquiryPrefill());
    const onPrefill = (e) => apply(e.detail);
    window.addEventListener(PREFILL_EVENT, onPrefill);
    return () => window.removeEventListener(PREFILL_EVENT, onPrefill);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/inquiry`, form);
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="inquire"
      data-testid="inquiry-section"
      className="on-obsidian relative overflow-hidden text-[#f6f0e6] px-6 md:px-12 lg:px-24 py-28 md:py-44"
      style={{
        background:
          "radial-gradient(44% 34% at 8% 8%, rgba(150,32,165,0.28) 0%, transparent 64%), radial-gradient(42% 30% at 92% 6%, rgba(130,24,150,0.22) 0%, transparent 66%), radial-gradient(52% 34% at 50% 100%, rgba(140,24,150,0.26) 0%, transparent 70%), linear-gradient(170deg, #0f0111 0%, #0a010c 55%, #08000c 100%)",
      }}
    >
      {/* Approved purple chrome-ribbon artwork — edges only, center kept dark for form contrast */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
        <img
          src="/brand/inquiry-purple-ribbons.webp"
          alt=""
          className="w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(170deg, rgba(15,1,17,0.35) 0%, rgba(10,1,12,0.45) 55%, rgba(8,0,12,0.4) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 grid grid-cols-12 gap-8">
        {/* Iridescent DE emblem watermark — faint, oversized, behind the editorial column */}
        <img
          src="/brand/de-emblem-iridescent.png"
          alt=""
          aria-hidden="true"
          data-testid="inquiry-emblem-watermark"
          className="pointer-events-none absolute -left-16 md:-left-28 top-1/2 -translate-y-1/2 w-[380px] md:w-[560px] opacity-[0.10] select-none"
        />
        <Reveal className="col-span-12 md:col-span-5">
          <p className="text-[11px] uppercase tracking-[0.34em] text-charcoal-mute mb-6">Inquiry</p>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Begin the<br />
            <span className="italic text-chrome-lavender">next edit.</span>
          </h2>
          <p className="mt-8 max-w-sm text-sm md:text-base text-charcoal-soft leading-relaxed">
            Tell me about the brand you're building. Every inquiry is read personally and
            answered within two business days.
          </p>
          <p className="mt-8 text-sm tracking-[0.14em] text-charcoal-soft">
            <a data-testid="inquiry-email-link" href="mailto:dani@danielleedit.com" className="font-serif italic text-lg text-[#e5b8d6] hover:text-[#f6f0e6] transition-colors duration-300">
              dani@danielleedit.com
            </a>
          </p>
          <hr className="chrome-rule mt-12 max-w-sm" />
        </Reveal>

        <Reveal delay={0.15} className="col-span-12 md:col-span-6 md:col-start-7">
          {status === "sent" ? (
            <div data-testid="inquiry-success-message" className="border border-[#f6f0e6]/15 bg-[#f6f0e6]/[0.04] px-8 py-14 md:px-14 md:py-20">
              <p className="font-serif italic text-3xl md:text-4xl leading-tight">Received, with thanks.</p>
              <p className="mt-6 text-sm md:text-base text-charcoal-soft leading-relaxed">
                Your inquiry is in Danielle's inbox. Expect a personal reply within two
                business days.
              </p>
              <button
                data-testid="inquiry-reset-button"
                onClick={() => {
                  setForm({
                    name: "", email: "", brand: "", project_type: projectTypes[0],
                    scope: "", timeline: timelines[1], budget: "",
                  });
                  setStatus("idle");
                }}
                className="mt-10 text-[10px] uppercase tracking-[0.3em] text-charcoal-mute border-b border-[#f6f0e6]/30 pb-1 hover:text-[#f6f0e6] hover:border-[#f6f0e6] transition-colors duration-300"
              >
                Write another inquiry
              </button>
            </div>
          ) : (
            <form data-testid="inquiry-form" onSubmit={submit} className="space-y-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
                <div>
                  <Label>Name</Label>
                  <input data-testid="inquiry-name-input" required value={form.name} onChange={set("name")}
                    className="field-editorial" placeholder="Your name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <input data-testid="inquiry-email-input" required type="email" value={form.email} onChange={set("email")}
                    className="field-editorial" placeholder="you@brand.com" />
                </div>
                <div>
                  <Label>Brand</Label>
                  <input data-testid="inquiry-brand-input" value={form.brand} onChange={set("brand")}
                    className="field-editorial" placeholder="Brand or company" />
                </div>
                <div>
                  <Label>Project type</Label>
                  <select data-testid="inquiry-project-type-select" value={form.project_type} onChange={set("project_type")}
                    className="field-editorial cursor-pointer">
                    {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    {extraType && !projectTypes.includes(extraType) && (
                      <option value={extraType}>{extraType}</option>
                    )}
                  </select>
                </div>
                <div>
                  <Label>Timeline</Label>
                  <select data-testid="inquiry-timeline-select" value={form.timeline} onChange={set("timeline")}
                    className="field-editorial cursor-pointer">
                    {timelines.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label optional>Budget range</Label>
                  <select data-testid="inquiry-budget-select" value={form.budget} onChange={set("budget")}
                    className="field-editorial cursor-pointer">
                    <option value="">Prefer to discuss</option>
                    {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Scope</Label>
                <textarea data-testid="inquiry-scope-input" required rows={4} value={form.scope} onChange={set("scope")}
                  className="field-editorial resize-none"
                  placeholder="What are we making, and why now?" />
              </div>

              <div className="flex items-start gap-4">
                <input
                  data-testid="inquiry-consent-checkbox"
                  type="checkbox"
                  id="inquiry-consent"
                  checked={form.agreed}
                  onChange={(e) => setForm((f) => ({ ...f, agreed: e.target.checked }))}
                  className="mt-1 h-4 w-4 shrink-0 cursor-pointer appearance-none border border-[#f6f0e6]/40 bg-transparent checked:bg-[#c9a3e0] checked:border-[#c9a3e0] transition-colors duration-300"
                />
                <label htmlFor="inquiry-consent" className="text-xs md:text-sm leading-relaxed text-[#efe6f4]/70 cursor-pointer">
                  I understand that project scope, terms, and payment schedules are agreed in writing
                  before work begins, and I consent to The Danielle Edit storing and responding to my
                  inquiry.{" "}
                  <a href="/privacy" className="underline decoration-[#c9a3e0]/60 underline-offset-4 hover:text-[#e3c2bd] transition-colors duration-300">Privacy Policy</a>
                  {" · "}
                  <a href="/terms" className="underline decoration-[#c9a3e0]/60 underline-offset-4 hover:text-[#e3c2bd] transition-colors duration-300">Terms of Use</a>
                </label>
              </div>

              {status === "error" && (
                <p data-testid="inquiry-error-message" className="text-sm text-[#f0a8c8]">
                  Something interrupted the send. Your words matter — please try again, or
                  email dani@danielleedit.com directly.
                </p>
              )}

              <button
                data-testid="inquiry-submit-button"
                type="submit"
                disabled={status === "sending" || !form.agreed}
                className="group relative overflow-hidden bg-[#f6f0e6] text-[#160a17] text-[11px] uppercase tracking-[0.3em] rounded-full px-10 py-4 transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50 disabled:hover:scale-100"
              >
                <span className="relative z-10">{status === "sending" ? "Sending…" : "Send inquiry"}</span>
                <span aria-hidden="true"
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: "linear-gradient(100deg, #c9a3e0, #efd9f2, #e5b8d6, #f6f0e6, #b07ad4)" }} />
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
